<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, nextTick, ref, watch } from "vue"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/yup"
import * as yup from "yup"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import type { Building } from "@types"
import type { ParcelLocationMultiPolygon } from "@core/services/building.service"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useAuthStore } from "@core/stores/auth.store"
import type {
    ModelParcel,
    BuildingFormParcelMapExpose,
} from "@core/components/building/form/BuildingFormParcelMap.vue"

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    readonly: boolean
    /** Bâtiment en consultation seule : champs désactivés (pas `readonly`). */
    disabled?: boolean
    /** Émission pour ouvrir le flux « modifier l’adresse » (page parente) */
    onRequestChangeAddress: () => void
}>()

// ================================
// COMPOSABLES
// ================================
const buildingStore = useBuildingStore()
const authStore = useAuthStore()
const { building } = storeToRefs(buildingStore)
const { isEmployeWriteOrHigher } = storeToRefs(authStore)

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Identité du bâtiment",
            name: "Nom",
            address: "Adresse",
            level: "Niveau",
            levelCategoryTooltip:
                "Pour modifier la catégorie du bâtiment, vous devez modifier l'adresse du bâtiment.",
            usage: "Usage",
            save: "Enregistrer",
            cancel: "Annuler",
            validateParcel: "Valider la parcelle",
            parcelConfirmTitle: "Valider la parcelle cadastrale",
            parcelConfirmMessage:
                "Certaines informations du bâtiment (identité, usage, données techniques liées au cadastre, etc.) peuvent être mises à jour ou réécrites à partir des données récupérées sur cette parcelle. Souhaitez-vous continuer ?",
            parcelConfirmAccept: "Confirmer",
            parcelConfirmCancel: "Annuler",
            changeAddress: "Modifier l’adresse",
            validation: {
                nameRequired: "Le nom est obligatoire",
                maxLength: "Le texte ne doit pas dépasser {max} caractères.",
            },
        },
    },
})

const NAME_MAX = 200
const OCCUPATION_MAX = 200

const getValidationSchema = computed(() =>
    toTypedSchema(
        yup.object({
            name: yup
                .string()
                .trim()
                .max(NAME_MAX, t("validation.maxLength", { max: NAME_MAX }))
                .required(t("validation.nameRequired")),
            occupationUsage: yup
                .string()
                .max(
                    OCCUPATION_MAX,
                    t("validation.maxLength", { max: OCCUPATION_MAX })
                )
                .optional(),
        })
    )
)

/**
 * Relie l'état d'erreur vee-validate à la prop PrimeVue `invalid`.
 */
function getPrimeInvalidFieldProps(state: { errors: string[] }) {
    return {
        props: {
            invalid: state.errors.length > 0,
        },
    }
}

const { handleSubmit, defineField, resetForm, setFieldValue, errors } = useForm(
    {
        validationSchema: getValidationSchema,
        initialValues: {
            name: "",
            occupationUsage: "",
        },
    }
)

const [nameField, nameFieldAttrs] = defineField(
    "name",
    getPrimeInvalidFieldProps
)
const [occupationField, occupationFieldAttrs] = defineField(
    "occupationUsage",
    getPrimeInvalidFieldProps
)

const loading = ref(false)
const parcelLoading = ref(false)
const error = ref("")
const confirmParcelVisible = ref(false)

const parcelMapRef = ref<BuildingFormParcelMapExpose | null>(null)

const modelParcel = ref<ModelParcel>({
    parcelId: "",
    parcelGeometry: null,
})

function getBuildingMongoId(b: Building | null): string {
    return b?._id ?? b?._id_building ?? ""
}

const getAddressLocation = computed((): [number, number] | null => {
    const b = building.value
    if (!b?.location?.coordinates || b.location.coordinates.length < 2) {
        return null
    }
    const c = b.location.coordinates
    return [c[0], c[1]]
})

const getNiveau = computed(() => building.value?.infos?.niveau ?? "")

/**
 * Carte cadastrale : pas d’édition pour un bâtiment « groupe » (parcelle non modifiable).
 */
const getIsParcelMapReadonly = computed(
    () =>
        props.readonly ||
        Boolean(props.disabled) ||
        !isEmployeWriteOrHigher.value ||
        getNiveau.value === "groupe"
)

const getLevelLabel = computed(() => {
    const n = getNiveau.value
    const map: Record<string, string> = {
        groupe: "Groupe",
        immeuble: "Immeuble",
        appartement: "Appartement",
        maison: "Maison",
        maison_appartement: "Maison / appartement",
        maison_immeuble: "Maison / immeuble",
    }
    return map[n] ?? n ?? "—"
})

const getIsParcelValid = computed(() => {
    const b = building.value as Building & {
        infos?: {
            cadastre?: { id_building?: string; numero_parcelle?: string }
        }
        geometry?: {
            cadastre?: { id_building?: string; numero_parcelle?: string }
        }
    }
    const cad = b?.infos?.cadastre ?? b?.geometry?.cadastre
    return (
        !!(cad?.id_building || cad?.numero_parcelle) ||
        getNiveau.value === "groupe"
    )
})

/** Cadastre déjà enregistré : la carte reste en vue jusqu’au crayon ; sinon recherche parcelle directe. */
const getHasCadastreOnBuilding = computed((): boolean => {
    const b = building.value as Building & {
        infos?: {
            cadastre?: { id_building?: string; numero_parcelle?: string }
        }
        geometry?: {
            cadastre?: { id_building?: string; numero_parcelle?: string }
        }
    }
    if (!b) return false
    const cad = b.infos?.cadastre ?? b.geometry?.cadastre
    return !!(cad?.id_building || cad?.numero_parcelle)
})

/** Identifiant parcelle tel qu’enregistré sur le bâtiment (pour détecter une modification locale). */
const getSavedParcelId = computed((): string => {
    const b = building.value as Building & {
        infos?: {
            cadastre?: { id_building?: string; numero_parcelle?: string }
        }
        geometry?: {
            cadastre?: { id_building?: string; numero_parcelle?: string }
        }
    }
    if (!b) return ""
    const cad = b.infos?.cadastre ?? b.geometry?.cadastre
    return cad?.id_building ?? cad?.numero_parcelle ?? ""
})

/** La sélection sur la carte diffère de la parcelle persistée. */
const getHasParcelChanged = computed(
    () => modelParcel.value.parcelId !== getSavedParcelId.value
)

function syncFormFromBuilding(): void {
    const b = building.value
    if (!b) return
    setFieldValue("name", b.name ?? "")
    setFieldValue("occupationUsage", b.infos?.occupation?.usage ?? "")
    const ext = b as Building & {
        infos?: {
            cadastre?: {
                id_building?: string
                numero_parcelle?: string
                location?: unknown
            }
        }
        geometry?: {
            cadastre?: {
                id_building?: string
                numero_parcelle?: string
                location?: unknown
            }
        }
    }
    const cad = ext.infos?.cadastre
    modelParcel.value = {
        parcelId: cad?.id_building ?? cad?.numero_parcelle ?? "",
        parcelGeometry: (cad?.location ??
            null) as ModelParcel["parcelGeometry"],
    }
}

watch(building, syncFormFromBuilding, { immediate: true, deep: true })

const onSubmit = handleSubmit(async (values) => {
    const b = building.value
    if (!b || props.readonly || props.disabled) return
    try {
        loading.value = true
        error.value = ""
        const id = getBuildingMongoId(b)
        const payload: Record<string, unknown> = {
            name: values.name,
        }
        if (values.occupationUsage !== (b.infos?.occupation?.usage ?? "")) {
            payload["infos.occupation.usage"] = values.occupationUsage
        }
        await buildingStore.updateBuildingById(id, payload, { silent: true })
    } catch (e) {
        error.value = (e as Error).message
    } finally {
        loading.value = false
    }
})

function onCancel(): void {
    syncFormFromBuilding()
    resetForm({
        values: {
            name: building.value?.name ?? "",
            occupationUsage: building.value?.infos?.occupation?.usage ?? "",
        },
    })
}

function onOpenConfirmValidateParcel(): void {
    if (!modelParcel.value.parcelId) return
    confirmParcelVisible.value = true
}

async function onConfirmValidateParcel(): Promise<void> {
    const b = building.value
    if (!b || !modelParcel.value.parcelId) return
    try {
        parcelLoading.value = true
        error.value = ""
        const id = getBuildingMongoId(b)
        const geom = modelParcel.value.parcelGeometry
        const parcelLocation =
            geom &&
            typeof geom === "object" &&
            "type" in geom &&
            geom.type === "MultiPolygon" &&
            Array.isArray((geom as { coordinates?: unknown }).coordinates)
                ? (geom as ParcelLocationMultiPolygon)
                : undefined
        await buildingStore.validateBuildingParcel(
            id,
            modelParcel.value.parcelId,
            parcelLocation
        )
        await nextTick()
        parcelMapRef.value?.exitParcelEditMode()
        confirmParcelVisible.value = false
    } catch (e) {
        error.value = (e as Error).message
    } finally {
        parcelLoading.value = false
    }
}

const getHasChanged = computed(() => {
    const b = building.value
    if (!b) return false
    return (
        nameField.value !== b.name ||
        occupationField.value !== (b.infos?.occupation?.usage ?? "")
    )
})
</script>

<template>
    <BaseTitledCard
        class="building-info-general-address w-full shadow-sm"
        content-class="space-y-6"
    >
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <form
                class="flex flex-col gap-6"
                @submit.prevent="onSubmit"
                @reset.prevent="onCancel"
            >
                <div
                    class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full"
                >
                    <BaseInputWrapper
                        :label="t('name')"
                        input-id="building-info-general-address-name"
                        :error="errors.name ?? ''"
                        unfloat
                    >
                        <BaseInputText
                            v-model="nameField"
                            v-bind="nameFieldAttrs"
                            input-id="building-info-general-address-name"
                            class="w-full"
                            :disabled="disabled"
                            :readonly="
                                (readonly || !isEmployeWriteOrHigher) &&
                                !disabled
                            "
                        />
                    </BaseInputWrapper>
                    <div>
                        <BaseInputWrapper
                            :label="t('address')"
                            input-id="building-info-general-address-inline-name"
                            unfloat
                        >
                            <BaseInputText
                                :model-value="
                                    building?.address?.inline_name ?? ''
                                "
                                id="building-info-general-address-inline-name"
                                class="w-full"
                                readonly
                                :disabled="disabled"
                            />
                            <template
                                v-if="
                                    !disabled &&
                                    !readonly &&
                                    isEmployeWriteOrHigher
                                "
                                #suffix
                            >
                                <BaseInputGroupAddon>
                                    <BaseButton
                                        type="button"
                                        severity="secondary"
                                        icon="pi pi-pencil"
                                        v-tooltip.top="t('changeAddress')"
                                        @click="() => onRequestChangeAddress()"
                                    />
                                </BaseInputGroupAddon>
                            </template>
                        </BaseInputWrapper>
                    </div>
                    <BaseInputWrapper
                        :label="t('level')"
                        input-id="building-info-general-address-level"
                        unfloat
                    >
                        <BaseInputText
                            :model-value="getLevelLabel"
                            input-id="building-info-general-address-level"
                            class="w-full"
                            disabled
                        />
                        <template #suffix v-if="!disabled">
                            <BaseInputGroupAddon>
                                <i
                                    class="pi pi-info-circle"
                                    v-tooltip.top="t('levelCategoryTooltip')"
                                ></i>
                            </BaseInputGroupAddon>
                        </template>
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :label="t('usage')"
                        input-id="building-occupation"
                        :error="errors.occupationUsage ?? ''"
                        unfloat
                    >
                        <InputBuildingOccupation
                            v-model="occupationField"
                            v-bind="occupationFieldAttrs"
                            :disabled="
                                readonly || disabled || !isEmployeWriteOrHigher
                            "
                        />
                    </BaseInputWrapper>
                </div>
                <div
                    v-if="
                        getHasChanged &&
                        !readonly &&
                        !disabled &&
                        isEmployeWriteOrHigher
                    "
                    class="flex flex-wrap justify-center gap-3"
                >
                    <BaseButton
                        type="reset"
                        :label="t('cancel')"
                        severity="secondary"
                    />
                    <BaseButton
                        type="submit"
                        :label="t('save')"
                        :loading="loading"
                    />
                </div>
                <BaseMessage v-if="error" severity="error" :closable="false">
                    {{ error }}
                </BaseMessage>
            </form>

            <div v-if="building">
                <BuildingFormParcelMap
                    ref="parcelMapRef"
                    :model-parcel="modelParcel"
                    :model-skip-parcel="false"
                    :readonly="getIsParcelMapReadonly"
                    :address-inline="building.address?.inline_name ?? ''"
                    :address-location="getAddressLocation"
                    :parcel-search-requires-edit-click="
                        getHasCadastreOnBuilding
                    "
                    disabled-skip-button
                    :show-underground-bt-hta-lines="true"
                    @update:model-parcel="modelParcel = $event"
                />
                <div
                    v-if="
                        (!getIsParcelValid || getHasParcelChanged) &&
                        !getIsParcelMapReadonly
                    "
                    class="flex justify-center mt-4"
                >
                    <BaseButton
                        type="button"
                        :label="t('validateParcel')"
                        :loading="parcelLoading"
                        :disabled="!modelParcel.parcelId"
                        @click="onOpenConfirmValidateParcel"
                    />
                </div>
            </div>

            <BaseDialog
                v-model:visible="confirmParcelVisible"
                modal
                :header="t('parcelConfirmTitle')"
                class="w-full max-w-md"
                :closable="!parcelLoading"
                :dismissable-mask="!parcelLoading"
                :close-on-escape="!parcelLoading"
            >
                <p class="text-sm text-surface-800 leading-relaxed m-0">
                    {{ t("parcelConfirmMessage") }}
                </p>
                <template #footer>
                    <div class="flex justify-end gap-2 flex-wrap">
                        <BaseButton
                            type="button"
                            :label="t('parcelConfirmCancel')"
                            severity="secondary"
                            :disabled="parcelLoading"
                            @click="confirmParcelVisible = false"
                        />
                        <BaseButton
                            type="button"
                            :label="t('parcelConfirmAccept')"
                            :loading="parcelLoading"
                            @click="onConfirmValidateParcel"
                        />
                    </div>
                </template>
            </BaseDialog>
        </template>
    </BaseTitledCard>
</template>
